import { useMemo } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import { EconomicModel, PaymentDirection, type FeeRowStatus } from "../types/paymentFees.types";
import type { FeeFormulaLabels, ScopeChipLabels } from "../utils/feeFormat";
import type { FeeChangeRole, FeeChangeTrend } from "../utils/feeHistory";

/**
 * Translated label bundles for the pure formatters in `utils/feeFormat.ts` (which cannot reach
 * `useTranslations`). One hook so the wording of a status, a scope chip or a formula can never diverge
 * between the corridors table, the rule groups, the create dialog and the delete confirmation.
 */
export interface FeeLabels {
  formula: FeeFormulaLabels;
  chips: ScopeChipLabels;
  direction: Record<PaymentDirection, string>;
  directionShort: { payin: string; payout: string };
  status: Record<FeeRowStatus, string>;
  model: Record<EconomicModel, string>;
  modelHelp: Record<EconomicModel, string>;
  role: Record<FeeChangeRole, string>;
  trend: Record<FeeChangeTrend, string>;
}

export function useFeeLabels(): FeeLabels {
  const { t } = useTranslations("paymentFees");

  return useMemo<FeeLabels>(
    () => ({
      formula: {
        anyCurrency: t("formula_any_currency"),
        min: t("formula_min"),
        max: t("formula_max"),
        ranges: (count) => t("formula_ranges", { count }),
        rangeFromTo: (from, to) => t("range_from_to", { from, to }),
        rangeFrom: (from) => t("range_from", { from }),
      },
      chips: {
        general: t("scope_general"),
        anyCostRoute: t("scope_any_cost_route"),
        anyCountry: t("scope_any_country"),
        anyRail: t("scope_any_rail"),
        anyCurrency: t("scope_any_currency"),
        anyDirection: t("scope_any_direction"),
        payin: t("direction_payin"),
        payout: t("direction_payout"),
        costRoute: (corridorCount) => t("scope_cost_route", { count: corridorCount }),
        costRouteInactive: t("scope_cost_route_inactive"),
      },
      direction: {
        [PaymentDirection.PAYIN]: t("direction_payin"),
        [PaymentDirection.PAYOUT]: t("direction_payout"),
      },
      directionShort: { payin: t("direction_payin"), payout: t("direction_payout") },
      status: {
        ACTIVE: t("status_active"),
        SCHEDULED: t("status_scheduled"),
        SUPERSEDED: t("status_superseded"),
        EXPIRED: t("status_expired"),
        DISABLED: t("status_disabled"),
      },
      model: {
        [EconomicModel.PASS_THROUGH]: t("model_pass_through"),
        [EconomicModel.ABSORBED]: t("model_absorbed"),
      },
      modelHelp: {
        [EconomicModel.PASS_THROUGH]: t("model_pass_through_help"),
        [EconomicModel.ABSORBED]: t("model_absorbed_help"),
      },
      role: {
        GENERAL: t("role_general"),
        SPECIAL: t("role_special"),
        COST: t("role_cost"),
      },
      trend: {
        NEW: t("trend_new"),
        UP: t("trend_up"),
        DOWN: t("trend_down"),
        SAME: t("trend_same"),
      },
    }),
    [t],
  );
}
